import React, { useCallback, useEffect, useState } from "react";
import {
  Briefcase,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Plus,
  Search,
  Star,
  Tag,
  Trash2,
  Upload,
} from "lucide-react";
import CMSLayout from "@/components/cms/CMSLayout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { fieldClass, labelClass } from "@/components/cms/formStyles";
import {
  createPortfolioCategory,
  deletePortfolioCategory,
  listPortfolioCategories,
  updatePortfolioCategory,
} from "@/lib/portfolioCategoriesApi";
import {
  createPortfolioItem,
  deletePortfolioItem,
  listPortfolioItems,
  updatePortfolioItem,
} from "@/lib/portfolioItemsApi";
import { uploadMedia } from "@/lib/mediaApi";

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyCategoryForm = { name: "", slug: "", is_active: true, order: 0 };
const emptyItemForm = {
  title: "",
  category_id: "",
  type: "",
  description: "",
  image_url: "",
  link_url: "",
  is_active: true,
  is_featured: false,
  order: 0,
};

const categoryToForm = (category) => ({
  name: category.name || "",
  slug: category.slug || "",
  is_active: Boolean(category.is_active),
  order: category.order ?? 0,
});

const itemToForm = (item) => ({
  title: item.title || "",
  category_id: item.category_id ? String(item.category_id) : "",
  type: item.type || "",
  description: item.description || "",
  image_url: item.image_url || "",
  link_url: item.link_url || "",
  is_active: Boolean(item.is_active),
  is_featured: Boolean(item.is_featured),
  order: item.order ?? 0,
});

const categoryFormToPayload = (form) => ({
  name: form.name,
  slug: form.slug,
  is_active: form.is_active,
  order: Number(form.order) || 0,
});

const itemFormToPayload = (form) => ({
  title: form.title,
  category_id: form.category_id || null,
  type: form.type || null,
  description: form.description || null,
  image_url: form.image_url || null,
  link_url: form.link_url || null,
  is_active: form.is_active,
  is_featured: form.is_featured,
  order: Number(form.order) || 0,
});

const PortfolioManagementPage = () => {
  const [activeTab, setActiveTab] = useState("items");
  const [notice, setNotice] = useState(null);

  // Categories
  const [categories, setCategories] = useState([]);
  const [categoriesPagination, setCategoriesPagination] = useState(null);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState("");

  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [categoryFormError, setCategoryFormError] = useState("");
  const [categorySaving, setCategorySaving] = useState(false);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);

  // Items
  const [items, setItems] = useState([]);
  const [itemsPagination, setItemsPagination] = useState(null);
  const [itemPage, setItemPage] = useState(1);
  const [itemSearch, setItemSearch] = useState("");
  const [itemCategoryFilter, setItemCategoryFilter] = useState("");
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState("");

  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [itemFormError, setItemFormError] = useState("");
  const [itemSaving, setItemSaving] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);
  const [brokenImages, setBrokenImages] = useState(() => new Set());
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const fetchCategories = useCallback(async (search, page) => {
    setCategoriesLoading(true);
    setCategoriesError("");
    try {
      const result = await listPortfolioCategories({ search, page });
      setCategories(result.categories);
      setCategoriesPagination(result.pagination);
    } catch (err) {
      setCategoriesError("Gagal memuat kategori. Periksa koneksi ke server.");
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async (search, categoryId, page) => {
    setItemsLoading(true);
    setItemsError("");
    try {
      const result = await listPortfolioItems({ search, categoryId, page });
      setItems(result.items);
      setItemsPagination(result.pagination);
    } catch (err) {
      setItemsError("Gagal memuat portofolio. Periksa koneksi ke server.");
    } finally {
      setItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCategories(categorySearch, categoryPage);
    }, 350);
    return () => clearTimeout(timeout);
  }, [categorySearch, categoryPage, fetchCategories]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchItems(itemSearch, itemCategoryFilter, itemPage);
    }, 350);
    return () => clearTimeout(timeout);
  }, [itemSearch, itemCategoryFilter, itemPage, fetchItems]);

  useEffect(() => {
    if (!notice) return;
    const timeout = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(timeout);
  }, [notice]);

  // Category form handlers
  const openCreateCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm(emptyCategoryForm);
    setSlugTouched(false);
    setCategoryFormError("");
    setIsCategoryFormOpen(true);
  };

  const openEditCategoryForm = (category) => {
    setEditingCategory(category);
    setCategoryForm(categoryToForm(category));
    setSlugTouched(true);
    setCategoryFormError("");
    setIsCategoryFormOpen(true);
  };

  const handleCategoryNameChange = (event) => {
    const name = event.target.value;
    setCategoryForm((prev) => ({
      ...prev,
      name,
      slug: slugTouched ? prev.slug : slugify(name),
    }));
  };

  const handleCategorySlugChange = (event) => {
    setSlugTouched(true);
    setCategoryForm((prev) => ({ ...prev, slug: event.target.value }));
  };

  const handleCategorySubmit = async (event) => {
    event.preventDefault();
    if (!categoryForm.name.trim() || !categoryForm.slug.trim()) {
      setCategoryFormError("Nama dan slug wajib diisi.");
      return;
    }
    setCategorySaving(true);
    setCategoryFormError("");
    try {
      const payload = categoryFormToPayload(categoryForm);
      if (editingCategory) {
        await updatePortfolioCategory(editingCategory.id, payload);
        setNotice({ type: "success", message: "Kategori berhasil diperbarui." });
      } else {
        await createPortfolioCategory(payload);
        setNotice({ type: "success", message: "Kategori berhasil ditambahkan." });
      }
      setIsCategoryFormOpen(false);
      fetchCategories(categorySearch, categoryPage);
    } catch (err) {
      setCategoryFormError(
        err.response?.data?.errors?.slug?.[0] ||
          err.response?.data?.message ||
          "Gagal menyimpan kategori. Coba lagi."
      );
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryTarget) return;
    setDeletingCategory(true);
    try {
      await deletePortfolioCategory(deleteCategoryTarget.id);
      setNotice({ type: "success", message: "Kategori berhasil dihapus." });
      setDeleteCategoryTarget(null);
      const isLastItemOnPage = categories.length === 1 && categoryPage > 1;
      fetchCategories(categorySearch, isLastItemOnPage ? categoryPage - 1 : categoryPage);
      if (isLastItemOnPage) setCategoryPage(categoryPage - 1);
    } catch (err) {
      setNotice({ type: "error", message: "Gagal menghapus kategori." });
    } finally {
      setDeletingCategory(false);
    }
  };

  const toggleCategoryActive = async (category) => {
    try {
      await updatePortfolioCategory(category.id, { is_active: !category.is_active });
      setCategories((prev) =>
        prev.map((c) => (c.id === category.id ? { ...c, is_active: !c.is_active } : c))
      );
    } catch (err) {
      setNotice({ type: "error", message: "Gagal mengubah status kategori." });
    }
  };

  // Item form handlers
  const openCreateItemForm = () => {
    setEditingItem(null);
    setItemForm(emptyItemForm);
    setItemFormError("");
    setUploadError("");
    setIsItemFormOpen(true);
  };

  const openEditItemForm = (item) => {
    setEditingItem(item);
    setItemForm(itemToForm(item));
    setItemFormError("");
    setUploadError("");
    setIsItemFormOpen(true);
  };

  const handleItemFormChange = (field) => (event) => {
    setItemForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadError("");
    setUploadingImage(true);
    try {
      const media = await uploadMedia(file);
      setItemForm((prev) => ({ ...prev, image_url: media.url }));
    } catch (err) {
      setUploadError(
        err.response?.data?.errors?.file?.[0] || "Gagal mengunggah gambar. Coba lagi."
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleItemSubmit = async (event) => {
    event.preventDefault();
    if (!itemForm.title.trim()) {
      setItemFormError("Judul wajib diisi.");
      return;
    }
    setItemSaving(true);
    setItemFormError("");
    try {
      const payload = itemFormToPayload(itemForm);
      if (editingItem) {
        await updatePortfolioItem(editingItem.id, payload);
        setNotice({ type: "success", message: "Portofolio berhasil diperbarui." });
      } else {
        await createPortfolioItem(payload);
        setNotice({ type: "success", message: "Portofolio berhasil ditambahkan." });
      }
      setIsItemFormOpen(false);
      fetchItems(itemSearch, itemCategoryFilter, itemPage);
    } catch (err) {
      setItemFormError(err.response?.data?.message || "Gagal menyimpan portofolio. Coba lagi.");
    } finally {
      setItemSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteItemTarget) return;
    setDeletingItem(true);
    try {
      await deletePortfolioItem(deleteItemTarget.id);
      setNotice({ type: "success", message: "Portofolio berhasil dihapus." });
      setDeleteItemTarget(null);
      const isLastItemOnPage = items.length === 1 && itemPage > 1;
      fetchItems(itemSearch, itemCategoryFilter, isLastItemOnPage ? itemPage - 1 : itemPage);
      if (isLastItemOnPage) setItemPage(itemPage - 1);
    } catch (err) {
      setNotice({ type: "error", message: "Gagal menghapus portofolio." });
    } finally {
      setDeletingItem(false);
    }
  };

  const toggleItemActive = async (item) => {
    try {
      await updatePortfolioItem(item.id, { is_active: !item.is_active });
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, is_active: !i.is_active } : i))
      );
    } catch (err) {
      setNotice({ type: "error", message: "Gagal mengubah status portofolio." });
    }
  };

  const toggleItemFeatured = async (item) => {
    try {
      await updatePortfolioItem(item.id, { is_featured: !item.is_featured });
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, is_featured: !i.is_featured } : i))
      );
    } catch (err) {
      setNotice({ type: "error", message: "Gagal mengubah status unggulan." });
    }
  };

  return (
    <CMSLayout
      active="Portfolio"
      breadcrumb="CMS / Portfolio"
      title="Manajemen Portfolio"
      headerActions={
        activeTab === "items" ? (
          <>
            <div className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-300">
              <Search size={16} />
              <input
                value={itemSearch}
                onChange={(e) => {
                  setItemPage(1);
                  setItemSearch(e.target.value);
                }}
                placeholder="Cari judul, deskripsi..."
                className="w-40 bg-transparent text-slate-100 placeholder:text-slate-500 focus:outline-none"
              />
            </div>
            <select
              value={itemCategoryFilter}
              onChange={(e) => {
                setItemPage(1);
                setItemCategoryFilter(e.target.value);
              }}
              className="rounded-full border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-300 focus:outline-none"
            >
              <option value="">Semua Kategori</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <button
              onClick={openCreateItemForm}
              className="flex items-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400"
            >
              <Plus size={16} /> Tambah Portfolio
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-300">
              <Search size={16} />
              <input
                value={categorySearch}
                onChange={(e) => {
                  setCategoryPage(1);
                  setCategorySearch(e.target.value);
                }}
                placeholder="Cari kategori..."
                className="w-40 bg-transparent text-slate-100 placeholder:text-slate-500 focus:outline-none"
              />
            </div>
            <button
              onClick={openCreateCategoryForm}
              className="flex items-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400"
            >
              <Plus size={16} /> Tambah Kategori
            </button>
          </>
        )
      }
    >
      {notice && (
        <div
          className={`rounded-2xl px-4 py-3 text-sm ${
            notice.type === "success"
              ? "bg-emerald-500/10 text-emerald-300"
              : "bg-red-500/10 text-red-400"
          }`}
        >
          {notice.message}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab("items")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
            activeTab === "items"
              ? "bg-cyan-500/15 text-cyan-300"
              : "text-slate-400 hover:bg-slate-800 hover:text-white"
          }`}
        >
          <Briefcase size={16} /> Item Portfolio
        </button>
        <button
          onClick={() => setActiveTab("categories")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
            activeTab === "categories"
              ? "bg-cyan-500/15 text-cyan-300"
              : "text-slate-400 hover:bg-slate-800 hover:text-white"
          }`}
        >
          <Tag size={16} /> Kategori
        </button>
      </div>

      {activeTab === "items" ? (
        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
          <div>
            <p className="text-sm text-slate-400">Karya & proyek</p>
            <h2 className="text-xl font-semibold text-white">
              {itemsPagination ? `${itemsPagination.total} Item Portfolio` : "Daftar Portfolio"}
            </h2>
          </div>

          {itemsLoading ? (
            <div className="mt-8 flex items-center justify-center gap-2 py-12 text-slate-400">
              <Loader2 size={18} className="animate-spin" /> Memuat data...
            </div>
          ) : itemsError ? (
            <div className="mt-6 rounded-2xl bg-red-500/10 p-4 text-sm text-red-400">{itemsError}</div>
          ) : items.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-700 p-8 text-center text-sm text-slate-400">
              Belum ada item portfolio. Klik "Tambah Portfolio" untuk menambahkan.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-950/70 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  {(() => {
                    const InfoWrapper = item.link_url ? "a" : "div";
                    const wrapperProps = item.link_url
                      ? {
                          href: item.link_url,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          title: `Buka ${item.link_url}`,
                        }
                      : {};
                    return (
                      <InfoWrapper
                        {...wrapperProps}
                        className={`flex items-center gap-4 rounded-xl ${
                          item.link_url ? "transition hover:bg-slate-900" : ""
                        }`}
                      >
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-800 text-slate-500">
                          {item.image_url && !brokenImages.has(item.id) ? (
                            <img
                              src={item.image_url}
                              alt={item.title}
                              className="h-full w-full object-cover"
                              onError={() =>
                                setBrokenImages((prev) => new Set(prev).add(item.id))
                              }
                            />
                          ) : (
                            <ImageIcon size={22} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{item.title}</p>
                            {item.is_featured && (
                              <Star size={14} className="fill-amber-400 text-amber-400" />
                            )}
                            {item.link_url && (
                              <ExternalLink size={13} className="text-slate-500" />
                            )}
                          </div>
                          <p className="text-sm text-slate-400">
                            {item.category?.name || "Tanpa kategori"}
                            {item.type ? ` • ${item.type}` : ""}
                          </p>
                        </div>
                      </InfoWrapper>
                    );
                  })()}

                  <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                    <button
                      onClick={() => toggleItemFeatured(item)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        item.is_featured
                          ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
                          : "border-slate-700 text-slate-400 hover:bg-slate-800"
                      }`}
                    >
                      <Star size={12} className={item.is_featured ? "fill-amber-400" : ""} />
                      Unggulan
                    </button>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={item.is_active}
                        onCheckedChange={() => toggleItemActive(item)}
                        className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-700"
                      />
                      <span className="text-xs text-slate-400">
                        {item.is_active ? "Aktif" : "Nonaktif"}
                      </span>
                    </div>
                    <button
                      onClick={() => openEditItemForm(item)}
                      className="rounded-full p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteItemTarget(item)}
                      className="rounded-full p-2 text-slate-400 transition hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {itemsPagination && itemsPagination.total_pages > 1 && (
            <div className="mt-6 flex items-center justify-between text-sm text-slate-400">
              <span>
                Halaman {itemsPagination.current_page} dari {itemsPagination.total_pages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={itemPage <= 1}
                  onClick={() => setItemPage((p) => Math.max(1, p - 1))}
                  className="rounded-full border border-slate-700 px-3 py-1.5 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Sebelumnya
                </button>
                <button
                  disabled={itemPage >= itemsPagination.total_pages}
                  onClick={() => setItemPage((p) => Math.min(itemsPagination.total_pages, p + 1))}
                  className="rounded-full border border-slate-700 px-3 py-1.5 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Berikutnya
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
          <div>
            <p className="text-sm text-slate-400">Pengelompokan portfolio</p>
            <h2 className="text-xl font-semibold text-white">
              {categoriesPagination ? `${categoriesPagination.total} Kategori` : "Daftar Kategori"}
            </h2>
          </div>

          {categoriesLoading ? (
            <div className="mt-8 flex items-center justify-center gap-2 py-12 text-slate-400">
              <Loader2 size={18} className="animate-spin" /> Memuat data...
            </div>
          ) : categoriesError ? (
            <div className="mt-6 rounded-2xl bg-red-500/10 p-4 text-sm text-red-400">
              {categoriesError}
            </div>
          ) : categories.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-700 p-8 text-center text-sm text-slate-400">
              Belum ada kategori. Klik "Tambah Kategori" untuk menambahkan.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-950/70 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-medium text-white">{category.name}</p>
                    <p className="text-sm text-slate-400">/{category.slug}</p>
                  </div>

                  <div className="flex items-center gap-4 sm:justify-end">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={category.is_active}
                        onCheckedChange={() => toggleCategoryActive(category)}
                        className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-700"
                      />
                      <span className="text-xs text-slate-400">
                        {category.is_active ? "Aktif" : "Nonaktif"}
                      </span>
                    </div>
                    <button
                      onClick={() => openEditCategoryForm(category)}
                      className="rounded-full p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteCategoryTarget(category)}
                      className="rounded-full p-2 text-slate-400 transition hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {categoriesPagination && categoriesPagination.total_pages > 1 && (
            <div className="mt-6 flex items-center justify-between text-sm text-slate-400">
              <span>
                Halaman {categoriesPagination.current_page} dari {categoriesPagination.total_pages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={categoryPage <= 1}
                  onClick={() => setCategoryPage((p) => Math.max(1, p - 1))}
                  className="rounded-full border border-slate-700 px-3 py-1.5 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Sebelumnya
                </button>
                <button
                  disabled={categoryPage >= categoriesPagination.total_pages}
                  onClick={() =>
                    setCategoryPage((p) => Math.min(categoriesPagination.total_pages, p + 1))
                  }
                  className="rounded-full border border-slate-700 px-3 py-1.5 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Berikutnya
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Item form modal */}
      <Dialog open={isItemFormOpen} onOpenChange={setIsItemFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? "Edit Portfolio" : "Tambah Portfolio"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Isi data proyek untuk ditampilkan di halaman portfolio website.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleItemSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Judul *</label>
                <input
                  className={fieldClass}
                  value={itemForm.title}
                  onChange={handleItemFormChange("title")}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Kategori</label>
                <select
                  className={fieldClass}
                  value={itemForm.category_id}
                  onChange={handleItemFormChange("category_id")}
                >
                  <option value="">Tanpa kategori</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Tipe</label>
                <input
                  className={fieldClass}
                  value={itemForm.type}
                  onChange={handleItemFormChange("type")}
                  placeholder="Video Production, Brand Design, ..."
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Gambar</label>
              <div className="flex items-center gap-3">
                {itemForm.image_url && (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-700 bg-slate-800 text-slate-500">
                    <img
                      key={itemForm.image_url}
                      src={itemForm.image_url}
                      alt="Preview"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    className={fieldClass}
                    value={itemForm.image_url}
                    onChange={handleItemFormChange("image_url")}
                    placeholder="https://... atau unggah file di bawah"
                  />
                  <label
                    className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-300 transition hover:bg-slate-800 ${
                      uploadingImage ? "pointer-events-none opacity-60" : ""
                    }`}
                  >
                    {uploadingImage ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    Unggah Gambar
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              </div>
              {uploadError && (
                <p className="mt-1.5 text-xs text-red-400">{uploadError}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>URL Tujuan</label>
              <input
                className={fieldClass}
                value={itemForm.link_url}
                onChange={handleItemFormChange("link_url")}
                placeholder="https://... (dibuka saat item ini diklik di daftar admin)"
              />
            </div>

            <div>
              <label className={labelClass}>Deskripsi</label>
              <textarea
                className={`${fieldClass} min-h-[80px]`}
                value={itemForm.description}
                onChange={handleItemFormChange("description")}
              />
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div>
                <label className={labelClass}>Urutan</label>
                <input
                  type="number"
                  className={`${fieldClass} w-24`}
                  value={itemForm.order}
                  onChange={handleItemFormChange("order")}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={itemForm.is_active}
                  onCheckedChange={(checked) =>
                    setItemForm((prev) => ({ ...prev, is_active: checked }))
                  }
                  className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-700"
                />
                <span className="text-sm text-slate-300">Aktif ditampilkan</span>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={itemForm.is_featured}
                  onCheckedChange={(checked) =>
                    setItemForm((prev) => ({ ...prev, is_featured: checked }))
                  }
                  className="data-[state=checked]:bg-amber-400 data-[state=unchecked]:bg-slate-700"
                />
                <span className="text-sm text-slate-300">Unggulan</span>
              </div>
            </div>

            {itemFormError && (
              <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {itemFormError}
              </p>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => setIsItemFormOpen(false)}
                className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:bg-slate-800"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={itemSaving}
                className="flex items-center justify-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {itemSaving && <Loader2 size={16} className="animate-spin" />}
                Simpan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category form modal */}
      <Dialog open={isCategoryFormOpen} onOpenChange={setIsCategoryFormOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingCategory ? "Edit Kategori" : "Tambah Kategori"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Kategori digunakan untuk mengelompokkan item portfolio.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Nama *</label>
              <input
                className={fieldClass}
                value={categoryForm.name}
                onChange={handleCategoryNameChange}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Slug *</label>
              <input
                className={fieldClass}
                value={categoryForm.slug}
                onChange={handleCategorySlugChange}
                required
              />
            </div>

            <div className="flex items-center gap-6">
              <div>
                <label className={labelClass}>Urutan</label>
                <input
                  type="number"
                  className={`${fieldClass} w-24`}
                  value={categoryForm.order}
                  onChange={(e) =>
                    setCategoryForm((prev) => ({ ...prev, order: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={categoryForm.is_active}
                  onCheckedChange={(checked) =>
                    setCategoryForm((prev) => ({ ...prev, is_active: checked }))
                  }
                  className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-slate-700"
                />
                <span className="text-sm text-slate-300">Aktif ditampilkan</span>
              </div>
            </div>

            {categoryFormError && (
              <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {categoryFormError}
              </p>
            )}

            <DialogFooter>
              <button
                type="button"
                onClick={() => setIsCategoryFormOpen(false)}
                className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:bg-slate-800"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={categorySaving}
                className="flex items-center justify-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {categorySaving && <Loader2 size={16} className="animate-spin" />}
                Simpan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete item confirm */}
      <AlertDialog
        open={Boolean(deleteItemTarget)}
        onOpenChange={(open) => !open && setDeleteItemTarget(null)}
      >
        <AlertDialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Hapus portfolio?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Tindakan ini akan menghapus "{deleteItemTarget?.title}" secara permanen dan tidak
              dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              disabled={deletingItem}
              className="bg-red-500 text-white hover:bg-red-400"
            >
              {deletingItem ? "Menghapus..." : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete category confirm */}
      <AlertDialog
        open={Boolean(deleteCategoryTarget)}
        onOpenChange={(open) => !open && setDeleteCategoryTarget(null)}
      >
        <AlertDialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Hapus kategori?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Menghapus "{deleteCategoryTarget?.name}" tidak akan menghapus item portfolio di
              dalamnya, tetapi kategorinya akan menjadi kosong.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              disabled={deletingCategory}
              className="bg-red-500 text-white hover:bg-red-400"
            >
              {deletingCategory ? "Menghapus..." : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CMSLayout>
  );
};

export default PortfolioManagementPage;
